import dayjs from 'dayjs';
import { Box, Typography, ButtonGroup, Button } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import CustomTimeZoneDateRangePicker from 'components/DatePIcker/CustomTimeZoneDateRangePicker';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { calculateSavings } from '../../DashboardV2/hooks/useKPI';
import { aggregateAssetWiseData } from '../utils/dataFormatters';
import { type HealthScore } from 'types/squares.type';
import { setSavingsData } from 'store/SquaresStore/DashboardSquaresState.slice';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { guessUserTimezone } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

const dateRangeList = ['Rolling 1 Yr', 'Custom'];

const getStartEndDateFromSelectedMonth = (monthYear: string) => {
  // Start of month
  const startDate = new Date(`${monthYear} 1`);

  // End of month (day 0 of next month = last day of current month)
  const endDate = new Date(
    startDate.getFullYear(),
    startDate.getMonth() + 1,
    0
  );
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
};

const getInitialDates = (timezoneBasedOnDisplayMode: string) => {
  const tz = timezoneBasedOnDisplayMode || guessUserTimezone();
  const initialStartDate = dayjs().tz(tz).subtract(1, 'year').utc().toDate();
  const initialEndDate = dayjs().tz(tz).endOf('day').utc().toDate();
  return { initialStartDate, initialEndDate };
};

// this is just done as the old api have formatter which convert to local
const adjustDateForLocalFormatting = (
  dateObj: Date,
  userTimezone?: string
): Date => {
  const tz = userTimezone ?? guessUserTimezone();
  return dayjs(dateObj).tz(tz).startOf('day').toDate();
};

export const FilterPanel = ({
  isLoading,
  setIsLoading,
  selectedMonth,
  setSelectedMonth,
}: {
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  selectedMonth: string | null;
  setSelectedMonth: Dispatch<SetStateAction<string | null>>;
}) => {
  const { t } = useTranslation();
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const customerId = useAppSelector((state) => state.persistedReducer).customer
    ?.id;

  const selectedRangeWorkOrderData = useAppSelector(
    (state) => state.squaresPartsMaintenanceExpenditureReducer.data?.currentData
  );

  const squaresDashboardAssetsData = useAppSelector(
    (state) => state.squaresDashboardReducer.assetDashboard.assets
  );
  const healthScoreData = useAppSelector(
    (state) => state.squaresDashboardReducer.healthScoreData
  );

  const squaresHandler = new SquaresDashboardHandler();

  const { initialStartDate, initialEndDate } = useMemo(() => {
    return getInitialDates(timezoneBasedOnDisplayMode);
  }, [timezoneBasedOnDisplayMode]);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [isSelected, setIsSelected] = useState('Rolling 1 Yr');
  const [shouldCalculateSavings, setShouldCalculateSavings] = useState(false);

  const getAssetItems = (paramHealthScoreData: HealthScore[]) => {
    if (paramHealthScoreData && Object.keys(paramHealthScoreData).length > 0) {
      const assetsArray = Object.values(paramHealthScoreData)
        .map((asset) => ({
          asset_details: asset.asset_details,
          component_details: asset.component_details,
        }))
        .filter((asset) => asset.asset_details?.health_score !== undefined);

      return assetsArray;
    } else {
      console.warn('healthScoreData is empty or invalid.');
      return [];
    }
  };

  const handleCalculateSavings = useCallback(
    (paramStartDate: Date, paramEndDate: Date) => {
      const aggregatedAssetData = aggregateAssetWiseData(
        squaresDashboardAssetsData
      );
      const assetsItems = getAssetItems(healthScoreData);

      const savings = calculateSavings({
        totalWorkOrders: selectedRangeWorkOrderData?.length ?? 0,
        numAssets: assetsItems.length,
        maintenanceHoursPerWO: 24,
        manufacturerFrequency: 121,
        applicationFrequency: 136,
        totalMaintenanceCost: aggregatedAssetData?.total_cost,
        tonsPerRound: 82.3,
        roundsPerDay: 20,
        startDate: dayjs(paramStartDate).format('YYYY-MM-DD'),
        endDate: dayjs(paramEndDate).format('YYYY-MM-DD'),
      });

      dispatch(
        setSavingsData({
          downtimeSaved:
            savings?.dateRange?.downtimeSaved ?? savings?.annual?.downtimeSaved,
          costSaved:
            savings?.dateRange?.costSaved ?? savings?.annual?.costSaved,
          productivityImproved:
            savings?.dateRange?.productivityImproved ??
            savings?.annual?.productivityImproved,
        })
      );
    },
    [squaresDashboardAssetsData, healthScoreData, selectedRangeWorkOrderData]
  );

  const handleFetchData = async (
    paramStartDate: Date,
    paramEndDate: Date,
    isMount: boolean
  ) => {
    try {
      const adjustedStartDate = adjustDateForLocalFormatting(
        paramStartDate,
        timezoneBasedOnDisplayMode
      );
      const adjustedEndDate = adjustDateForLocalFormatting(
        paramEndDate,
        timezoneBasedOnDisplayMode
      );
      setIsLoading(true);
      const promises: Array<Promise<any>> = [
        squaresHandler.getPartsMaintenanceExpenditure(
          adjustedStartDate,
          adjustedEndDate
        ),
        squaresHandler.getWorkOrdersWithDowntimeAggregated(
          adjustedStartDate,
          adjustedEndDate
        ),
        squaresHandler.getDashboardAssetsData(
          adjustedStartDate,
          adjustedEndDate
        ),
      ];

      if (isMount) {
        promises.push(squaresHandler.getHealthScoreData());
        promises.push(
          squaresHandler.getTarget(
            customerId,
            'squaresMaintenanceCost',
            'all',
            new Date().getFullYear()
          )
        );
      }

      await Promise.all(promises);
      setShouldCalculateSavings(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleSelect = (id: string) => {
    setIsSelected(id);
    let newStartDate = new Date();
    const newEndDate = dayjs().tz(timezoneBasedOnDisplayMode).utc().toDate();

    switch (id) {
      case 'Rolling 1 Yr':
        newStartDate = dayjs()
          .tz(timezoneBasedOnDisplayMode)
          .subtract(1, 'year')
          .utc()
          .toDate();

        break;
      case 'Custom':
        break;
      default:
        break;
    }

    if (id !== 'Custom') {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      setSelectedMonth(null);
      void handleFetchData(newStartDate, newEndDate, false);
    }
  };

  const handleApply = async () => {
    try {
      setSelectedMonth(null);
      await handleFetchData(startDate, endDate, false);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (accessToken) {
      void handleFetchData(startDate, endDate, true);
    }
  }, [accessToken]);

  // look for selected month changes
  useEffect(() => {
    if (selectedMonth) {
      const { startDate, endDate } =
        getStartEndDateFromSelectedMonth(selectedMonth);
      void handleFetchData(startDate, endDate, false);
    }
  }, [selectedMonth]);

  // to update savings
  useEffect(() => {
    if (shouldCalculateSavings) {
      handleCalculateSavings(startDate, endDate);
      setShouldCalculateSavings(false); // reset flag
    }
  }, [shouldCalculateSavings, startDate, endDate]);

  return (
    <Box
      sx={{
        display: 'flex',
        my: '10px',
        borderRadius: '5px',
      }}
    >
      <Typography
        style={{
          fontSize: '12px',
          marginRight: '10px',
          paddingTop: '7px',
          color: palette.textPrimary,
        }}
      >
        {t('quick_range_10')}
      </Typography>
      <ButtonGroup
        size="small"
        variant="outlined"
        aria-label={t('basic_button_group')}
        sx={{
          marginRight: '10px',
        }}
      >
        {dateRangeList.map((item) => {
          return (
            <Button
              key={item}
              size="small"
              style={{
                backgroundColor:
                  isSelected === item
                    ? palette.primary
                    : enableDarkTheme
                    ? palette.cartesianGrid
                    : palette.boxBackground,
                color:
                  isSelected === item ? palette.white : palette.textPrimary,
                textTransform: 'none',
              }}
              onClick={() => {
                handleSelect(item);
              }}
            >
              {item === 'Rolling 1 Yr'
                ? t('rolling_1yr')
                : t('date_range_custom')}
            </Button>
          );
        })}
      </ButtonGroup>
      {isSelected === 'Custom' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <CustomTimeZoneDateRangePicker
            startDate={startDate}
            endDate={endDate}
            maxDate={dayjs(new Date().getTime())}
            onFinalDateChange={handleDateChange}
          />

          <Button
            sx={{
              color: '#FFFFFF',
              backgroundColor: palette.primary,
              fontSize: '12px',
              textTransform: 'none',
              height: '30px',
              '&:hover': {
                color: enableDarkTheme ? 'white' : 'black',
                border: enableDarkTheme ? '1px solid white' : '1px solid black',
              },
            }}
            disabled={isLoading}
            onClick={handleApply}
          >
            {t('apply')}
          </Button>
        </div>
      )}
    </Box>
  );
};
